import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateQuestionConfigDto,
  FormQuestionDto,
  FormSectionDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_FORM_SECTIONS_CONFIG } from "../domain";

import { ACTION_GET_FORM_SECTIONS, DOMAIN_FORMS, roleGuard } from "@api";
import { formsSectionsConfigApi } from "@api/services";

export const ACTION_ADD_FORM_QUESTION_CONFIG = "ADD_FORM_QUESTION_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type CreateQuestionConfigProps = {
  sectionId: string;
  dto: CreateQuestionConfigDto;
};

const createQuestionToSection = async (
  props: CreateQuestionConfigProps,
): Promise<FormQuestionDto[]> => {
  const response =
    await formsSectionsConfigApi.formSectionsConfigControllerCreateQuestionToSection(
      props.sectionId,
      props.dto,
    );

  return response.data;
};

export const useCreateQuestionToSection = (
  user: UserProfileDto | null,
  formId: string,
  sectionId: string,
): {
  createQuestionToSection: UseMutateFunction<
    FormQuestionDto[],
    Error,
    CreateQuestionConfigProps,
    unknown
  >;
  createQuestionToSectionEnabled: boolean;
  isSuccessCreateQuestionToSection: boolean;
  isPendingCreateQuestionToSection: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const { mutate, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SECTIONS_CONFIG, ACTION_ADD_FORM_QUESTION_CONFIG],
    mutationFn: enabled ? createQuestionToSection : undefined,
    onSuccess(data) {
      const queryKey = [DOMAIN_FORMS, ACTION_GET_FORM_SECTIONS, formId];
      const currentData = queryClient.getQueryData<FormSectionDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        return currentData?.map((section) => ({
          ...section,
          formQuestions:
            section.id === sectionId
              ? section.formQuestions
                ? [...section.formQuestions, ...data]
                : [...data]
              : section.formQuestions,
        }));
      });
    },
    onError: () => {
      enqueueSnackbar(
        t(
          "forms.snackbar.questionCodeAlreadyExistsInSection",
          "Question with this code already exists in the section",
        ),
        { variant: "error" },
      );
    },
  });

  return {
    createQuestionToSection: mutate,
    createQuestionToSectionEnabled: enabled,
    isSuccessCreateQuestionToSection: isSuccess,
    isPendingCreateQuestionToSection: isPending,
  };
};
